<template>
  <svg
    ref="loadingSvg"
    :width="size"
    :height="size"
    :style="{ maxWidth: size + 'px', maxHeight: size + 'px' }"
    style="background: transparent; display: inline!important;"
    shape-rendering="crispEdges"
  >
    <g v-for="(candle, index) in candles" :key="index">
      <!-- Wick (for filled candles: continuous line) -->
      <line
        v-if="filledCandles[index]"
        :x1="candle.x + candleWidth / 2"
        :y1="candle.wickTopY"
        :x2="candle.x + candleWidth / 2"
        :y2="candle.wickBottomY"
        :stroke="shapeColor"
        :stroke-width="lineWidth"
      />
      <!-- Wick for hollow candles: parted (only outside the body) -->
      <template v-else>
        <!-- Upper wick -->
        <line
          :x1="candle.x + candleWidth / 2"
          :y1="candle.wickTopY"
          :x2="candle.x + candleWidth / 2"
          :y2="candle.topY"
          :stroke="shapeColor"
          :stroke-width="lineWidth"
        />
        <!-- Lower wick -->
        <line
          :x1="candle.x + candleWidth / 2"
          :y1="candle.bottomY"
          :x2="candle.x + candleWidth / 2"
          :y2="candle.wickBottomY"
          :stroke="shapeColor"
          :stroke-width="lineWidth"
        />
      </template>
      <!-- Candle body -->
      <rect
        :x="candle.x"
        :y="candle.topY"
        :width="candleWidth"
        :height="candle.bottomY - candle.topY"
        :fill="filledCandles[index] ? shapeColor : 'none'"
        :stroke="shapeColor"
        :stroke-width="lineWidth"
      />
    </g>
  </svg>
</template>

<script>
import { textactive } from "@/_core/_helpers/_parametric_tools";

export default {
  name: "QSLoading",
  props: {
    colorTheme: { required: true, type: Boolean, default: true },
    isLoading: {
      type: Boolean,
      default: true
    },
    size: {
      type: Number,
      default: 50 // Default size (width and height in pixels)
    }
  },
  data() {
    return {
      animationFrameId: null,
      animationProgress: 0, // Progress of current animation (0 to 1)
      animationSpeed: 0.2, // Speed of size change (adjust for faster/slower animation)
      animationState: "decreasing", // 'decreasing', 'increasing', 'paused'
      candleCount: 5,
      candles: [], // Reactive candle data for SVG rendering
      currentCandleIndex: 0, // Tracks which candle is animating
      filledCandles: [true, false, true, false, true], // Filled: yes, no, yes, no, yes
      pauseDuration: 500, // Pause duration in milliseconds (0.5 seconds)
      pauseTimer: 0, // Timer for the pause
      wickHeight: 0.75
    };
  },
  computed: {
    shapeColor() {
      return textactive(this.colorTheme);
    },
    // Scale dimensions proportionally to the canvas size
    candleWidth() {
      return this.size * 0.067 * 1.5; // Adjusted proportion
    },
    spacing() {
      return this.size * 0.067; // 40 / 600
    },
    centerY() {
      return this.size / 2;
    },
    maxHeights() {
      return [
        this.size * 0.1 * 2.4, // Adjusted proportion
        this.size * 0.2 * 2.4,
        this.size * 0.3 * 2.1,
        this.size * 0.2 * 2.4,
        this.size * 0.1 * 2.4
      ];
    },
    lineWidth() {
      return this.size * 0.0067 * 3; // Adjusted proportion, minimum 1 for visibility
    },
    totalWidth() {
      return (this.candleWidth + this.spacing) * (this.candleCount - 1);
    },
    startX() {
      return (this.size - this.totalWidth - this.candleWidth) / 2;
    }
  },
  methods: {
    initializeCandles() {
      // Initialize candles with their maximum heights
      this.candles = Array.from({ length: this.candleCount }, (_, i) => {
        const maxHeight = this.maxHeights[i];
        const height = maxHeight; // Start fully expanded
        const halfHeight = height / 2;
        const x = this.startX + i * (this.candleWidth + this.spacing);
        const topY = this.centerY - halfHeight;
        const bottomY = this.centerY + halfHeight;
        const wickHeight = height * this.wickHeight;
        const wickTopY = this.centerY - wickHeight;
        const wickBottomY = this.centerY + wickHeight;

        return { x, topY, bottomY, wickTopY, wickBottomY, height, maxHeight };
      });
    },
    updateCandle(index, progress) {
      // Update the height of the specified candle based on animation progress
      const candle = this.candles[index];
      const targetHeight =
        this.animationState === "paused"
          ? candle.maxHeight
          : this.animationState === "decreasing"
          ? candle.maxHeight * (1 - progress) // Decrease to 0
          : candle.maxHeight * progress; // Increase back to max
      const height = Math.max(0, targetHeight); // Ensure height doesn't go negative
      const halfHeight = height / 2;
      candle.height = height;
      candle.topY = this.centerY - halfHeight;
      candle.bottomY = this.centerY + halfHeight;
      const addHeight = index === 0 || index === 4 ? 1.34 : 1;
      const wickHeight = height * addHeight * this.wickHeight;
      candle.wickTopY = this.centerY - wickHeight;
      candle.wickBottomY = this.centerY + wickHeight;
    },
    animate() {
      if (!this.isLoading) {
        this.candles = [];
        return;
      }

      if (this.animationState === "paused") {
        // Ensure all candles are at max height during pause
        this.pauseTimer += 1000 / 60; // Approximate 60 FPS
        if (this.pauseTimer >= this.pauseDuration) {
          this.pauseTimer = 0;
          this.currentCandleIndex = 0;
          this.animationState = "decreasing";
          this.animationProgress = 0;
        }
        this.animationFrameId = requestAnimationFrame(this.animate);
        return;
      }

      // Update animation progress
      this.animationProgress += this.animationSpeed;
      if (this.animationProgress > 1) {
        this.animationProgress = 0;
        if (this.animationState === "decreasing") {
          this.animationState = "increasing";
        } else {
          // Finished increasing, move to next candle or pause
          this.currentCandleIndex++;
          if (this.currentCandleIndex >= this.candleCount) {
            this.animationState = "paused";
            this.currentCandleIndex = 0;
          } else {
            this.animationState = "decreasing";
          }
        }
      }

      // Update the current candle
      this.updateCandle(this.currentCandleIndex, this.animationProgress);

      // Request next frame
      this.animationFrameId = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.initializeCandles();
    this.animate();
  },
  beforeDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  },
  watch: {
    isLoading(newVal) {
      if (newVal && !this.animationFrameId) {
        this.initializeCandles();
        this.animate();
      }
    },
    size() {
      // Reinitialize candles if size changes
      this.initializeCandles();
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animate();
      }
    }
  }
};
</script>

<style scoped>
svg {
  display: block;
  width: 100%;
  height: 100%;
  margin: 0 auto;
}
</style>
